import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import {
  Waves,
  SignOut,
  CreditCard,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
  Spinner,
  ArrowClockwise,
  List,
  PlusCircle,
  Copy,
  Eye,
  CaretDown,
  CaretUp,
  Receipt,
  Lightning,
  ShoppingCart,
  CurrencyBtc,
  User,
  Question,
  Lock,
  Envelope,
  PencilSimple,
  MagnifyingGlass,
  ListBullets,
  Code,
  Key,
  EyeSlash,
  Sun,
  Moon
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';
import { Logo, LogoText } from '../components/Logo';

const calculateStage = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const elapsed = (now - created) / 1000;
  if (elapsed >= 60) return 'complete';
  if (elapsed >= 30) return 'submitted';
  if (elapsed >= 15) return 'google_crawl';
  return 'processing';
};

const IndexingStage = ({ stage, createdAt }) => {
  const [currentStage, setCurrentStage] = useState(stage);

  useEffect(() => {
    setCurrentStage(calculateStage(createdAt));
    const interval = setInterval(() => setCurrentStage(calculateStage(createdAt)), 5000);
    return () => clearInterval(interval);
  }, [createdAt, stage]);

  const stages = [
    { id: 'processing', label: 'Processing', time: '0-5s', color: 'bg-amber-500' },
    { id: 'google_crawl', label: 'Google Crawl', time: '5-15s', color: 'bg-blue-500' },
    { id: 'submitted', label: 'Submitted', time: '15-30s', color: 'bg-purple-500' },
    { id: 'complete', label: 'Complete', time: '30-60s', color: 'bg-emerald-500' }
  ];
  const currentIndex = stages.findIndex(s => s.id === currentStage);

  return (
    <div className="flex items-center gap-1">
      {stages.map((s, i) => (
        <div key={s.id} className="flex items-center">
          <div className={`w-2 h-2 rounded-full transition-colors ${i <= currentIndex ? s.color : 'bg-slate-600'}`} title={`${s.label} (${s.time})`} />
          {i < stages.length - 1 && <div className={`w-4 h-0.5 transition-colors ${i < currentIndex ? 'bg-slate-400' : 'bg-slate-700'}`} />}
        </div>
      ))}
      <span className={`ml-2 text-xs font-medium ${currentStage === 'complete' ? 'text-emerald-400' : 'text-amber-400'}`}>
        {stages.find(s => s.id === currentStage)?.label || 'Processing'}
      </span>
    </div>
  );
};

const BatchStatusBadge = ({ createdAt, status }) => {
  const [currentStatus, setCurrentStatus] = useState(status);
  useEffect(() => {
    const check = () => {
      const elapsed = (new Date() - new Date(createdAt)) / 1000;
      setCurrentStatus(elapsed >= 60 ? 'complete' : 'processing');
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, [createdAt, status]);

  if (currentStatus === 'complete') {
    return <span className="badge-success flex items-center gap-1"><CheckCircle weight="fill" className="w-3 h-3" /> Complete</span>;
  }
  return <span className="badge-processing flex items-center gap-1"><Spinner className="w-3 h-3 animate-spin" /> Processing</span>;
};

const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: Globe },
  { id: 'history', label: 'Indexing History', icon: ListBullets },
  { id: 'purchases', label: 'Purchase History', icon: ShoppingCart },
  { id: 'buy', label: 'Buy Credits', icon: Lightning, isLink: true, to: '/pricing' },
  { id: 'api-docs', label: 'API Docs', icon: Code, isLink: true, to: '/docs' },
  { id: 'support', label: 'Support', icon: Question, isLink: true, to: '/support' },
  { id: 'profile', label: 'Profile', icon: User },
];

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [urls, setUrls] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Profile state
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // API Key state
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchPurchaseHistory();
    fetchCreditHistory();
    fetchApiKey();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfileEmail(user.email || '');
    }
  }, [user]);

  const fetchSubmissions = async () => {
    setLoadingSubmissions(true);
    try {
      const response = await axios.get(`${API_URL}/api/urls/submissions`, { withCredentials: true });
      setSubmissions(response.data);
    } catch (err) { console.error('Error fetching submissions:', err); }
    setLoadingSubmissions(false);
  };

  const fetchPurchaseHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/history`, { withCredentials: true });
      setPurchaseHistory(response.data);
    } catch (err) { console.error('Error fetching purchase history:', err); }
  };

  const fetchCreditHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/credit-history`, { withCredentials: true });
      setCreditHistory(response.data);
    } catch (err) { console.error('Error fetching credit history:', err); }
  };

  const fetchApiKey = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/api-key`, { withCredentials: true });
      if (response.data.key) setApiKey(response.data);
    } catch (err) { console.error('Error fetching API key:', err); }
  };

  const handleGenerateApiKey = async () => {
    setGeneratingKey(true);
    try {
      const response = await axios.post(`${API_URL}/api/user/api-key`, {}, { withCredentials: true });
      setApiKey(response.data);
      setApiKeyVisible(true);
      toast.success('New API key generated!');
    } catch (err) { toast.error('Failed to generate API key'); }
    setGeneratingKey(false);
  };

  const copyApiKey = () => {
    if (apiKey?.key) {
      navigator.clipboard.writeText(apiKey.key);
      setApiKeyCopied(true);
      toast.success('API key copied!');
      setTimeout(() => setApiKeyCopied(false), 2000);
    }
  };

  const refreshSubmissionStatus = async (submissionId) => {
    try {
      await axios.get(`${API_URL}/api/urls/refresh-status/${submissionId}`, { withCredentials: true });
      fetchSubmissions();
    } catch (err) { console.error('Error refreshing status:', err); }
  };

  const handleSubmitUrls = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    const urlList = urls.split('\n').map(u => u.trim()).filter(u => u.length > 0);
    if (urlList.length === 0) { setError('Please enter at least one URL'); return; }
    if (urlList.length > 150) { setError('Maximum 150 URLs per submission'); return; }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/urls/submit`, { urls: urlList }, { withCredentials: true });
      setSuccess(`Successfully submitted ${urlList.length} URL(s)! Credits used: ${response.data.credits_used}`);
      setUrls('');
      fetchSubmissions();
      refreshUser();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to submit URLs'); }
    setSubmitting(false);
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/user/profile`, { name: profileName, email: profileEmail }, { withCredentials: true });
      toast.success('Profile updated!');
      refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update profile'); }
    setSavingProfile(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/api/user/password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
      toast.success('Password changed!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    setChangingPassword(false);
  };

  const copyAllUrls = (urlList) => { navigator.clipboard.writeText(urlList.join('\n')); setCopiedId('all'); setTimeout(() => setCopiedId(null), 2000); };
  const copyUrl = (url, id) => { navigator.clipboard.writeText(url); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); };
  const urlCount = urls.split('\n').filter(u => u.trim().length > 0).length;

  const getPaymentStatusBadge = (status) => {
    const styles = {
      finished: 'bg-emerald-500/20 text-emerald-400',
      waiting: 'bg-amber-500/20 text-amber-400',
      confirming: 'bg-blue-500/20 text-blue-400',
      expired: 'bg-red-500/20 text-red-400',
      failed: 'bg-red-500/20 text-red-400',
    };
    const labels = { finished: 'Completed', waiting: 'Waiting', confirming: 'Confirming', expired: 'Expired', failed: 'Failed' };
    return <span className={`px-2 py-1 text-xs rounded ${styles[status] || 'bg-slate-500/20 text-slate-400'}`}>{labels[status] || status}</span>;
  };

  // Merge admin credit entries into a unified transaction list for purchase history
  const allTransactions = [
    ...purchaseHistory.map(p => ({ ...p, source: 'purchase' })),
    ...creditHistory.filter(c => c.type === 'admin_add' || c.type === 'admin_subtract').map(c => ({
      ...c,
      source: 'admin_credit',
      credits: c.amount,
      status: c.type === 'admin_add' ? 'credited' : 'debited'
    }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-64 border-r z-50 transition-transform duration-300 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} style={{ background: 'var(--bg-sidebar)', borderColor: 'var(--border-main)' }} data-testid="sidebar">
        {/* Logo */}
        <div className="p-5 border-b" style={{ borderColor: 'var(--border-main)' }}>
          <Link to="/" className="flex items-center gap-3">
            <Logo size="sm" />
            <LogoText size="md" />
          </Link>
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {SIDEBAR_ITEMS.map(item => {
            const Icon = item.icon;
            if (item.isLink) {
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid={`sidebar-${item.id}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === item.id
                    ? 'text-[var(--accent)]'
                    : ''
                }`}
                style={activeSection === item.id
                  ? { background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }
                  : { color: 'var(--text-secondary)' }
                }
                data-testid={`sidebar-${item.id}`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Bottom: Credits + Logout */}
        <div className="p-4 border-t space-y-3" style={{ borderColor: 'var(--border-main)' }}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-main)' }}>
            <CreditCard weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            <div>
              <p className="font-bold text-sm">{user?.credits || 0}</p>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>credits</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="logout-button"
          >
            <SignOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <div className="sticky top-0 z-30 backdrop-blur-xl border-b px-3 lg:px-8 py-3 flex items-center justify-between gap-2" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)', overflowX: 'auto' }}>
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 rounded-lg flex-shrink-0" data-testid="mobile-menu-btn">
              <List className="w-5 h-5" />
            </button>
            <h1 className="text-sm lg:text-lg font-bold capitalize truncate">{activeSection === 'dashboard' ? `Welcome, ${user?.name || 'User'}` : SIDEBAR_ITEMS.find(i => i.id === activeSection)?.label}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-colors flex-shrink-0"
              style={{ color: 'var(--text-secondary)' }}
              data-testid="theme-toggle-btn"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/pricing" className="btn-primary text-xs px-2.5 py-1.5 lg:px-3 lg:py-2 flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap" data-testid="buy-credits-top">
              <Lightning weight="fill" className="w-4 h-4" /> <span className="hidden sm:inline">Buy</span> Credits
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-2.5 py-1.5 lg:px-3 lg:py-2 rounded-lg text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
              data-testid="logout-button-top"
            >
              <SignOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        <div className="p-4 lg:p-8 max-w-6xl">
          {/* ========= DASHBOARD ========= */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {/* API Key Banner */}
              <div className="border rounded-xl p-3 lg:p-5" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }} data-testid="api-key-section">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
                      <Key weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: 'var(--accent)' }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold">Your API Key</h3>
                      <p className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>Use this key for programmatic API access</p>
                    </div>
                  </div>
                  <button
                    onClick={handleGenerateApiKey}
                    disabled={generatingKey}
                    className="text-xs px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 self-start sm:self-auto flex-shrink-0"
                    style={{ background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--border-accent)' }}
                    data-testid="generate-api-key-btn"
                  >
                    {generatingKey ? <Spinner className="w-3 h-3 animate-spin" /> : <ArrowClockwise className="w-3 h-3" />}
                    {apiKey ? 'Regenerate' : 'Generate'}
                  </button>
                </div>
                {apiKey?.key ? (
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="flex-1 border rounded-lg px-2 sm:px-3 py-2 font-mono text-[10px] sm:text-xs overflow-hidden text-ellipsis whitespace-nowrap" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }}>
                      {apiKeyVisible ? apiKey.key : `${apiKey.key.slice(0, 6)}${'*'.repeat(20)}${apiKey.key.slice(-4)}`}
                    </div>
                    <button
                      onClick={() => setApiKeyVisible(!apiKeyVisible)}
                      className="p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0"
                      style={{ color: 'var(--text-secondary)' }}
                      data-testid="toggle-api-key-visibility"
                      title={apiKeyVisible ? 'Hide' : 'Show'}
                    >
                      {apiKeyVisible ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={copyApiKey}
                      className="p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0"
                      style={{ color: 'var(--text-secondary)' }}
                      data-testid="copy-api-key-btn"
                      title="Copy"
                    >
                      {apiKeyCopied ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                ) : (
                  <p className="text-xs border border-dashed rounded-lg px-3 py-2.5 text-center" style={{ color: 'var(--text-muted)', background: 'var(--bg-input)', borderColor: 'var(--text-dimmed)' }}>No API key yet. Click "Generate" to create one.</p>
                )}
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="card-dashboard" data-testid="stat-credits">
                  <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-bg)' }}>
                      <CreditCard weight="duotone" className="w-4 h-4 sm:w-6 sm:h-6" style={{ color: 'var(--accent)' }} />
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-lg sm:text-2xl font-black">{user?.credits || 0}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">Credits</p>
                    </div>
                  </div>
                </div>
                <div className="card-dashboard" data-testid="stat-batches">
                  <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Globe weight="duotone" className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-400" />
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-lg sm:text-2xl font-black">{submissions.length}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">Batches</p>
                    </div>
                  </div>
                </div>
                <div className="card-dashboard" data-testid="stat-time">
                  <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock weight="duotone" className="w-4 h-4 sm:w-6 sm:h-6 text-amber-400" />
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-lg sm:text-2xl font-black">30-60s</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">Avg Time</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* URL Submit Form */}
              <div className="border p-4 lg:p-6 rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                <h2 className="text-base sm:text-lg font-bold mb-3 flex items-center gap-2">
                  <Globe weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                  Bulk URL Indexing
                </h2>
                <p className="text-xs sm:text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Submit up to 150 URLs per batch</p>

                {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2 text-xs sm:text-sm mb-3 rounded-lg" data-testid="submit-error">{error}</div>}
                {success && <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-3 py-2 text-xs sm:text-sm mb-3 rounded-lg" data-testid="submit-success">{success}</div>}

                <form onSubmit={handleSubmitUrls} data-testid="url-submit-form">
                  <textarea
                    value={urls}
                    onChange={(e) => setUrls(e.target.value)}
                    className="input-field min-h-[140px] sm:min-h-[180px] font-mono text-xs sm:text-sm resize-none mb-3"
                    placeholder={"https://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3"}
                    data-testid="url-textarea"
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-xs sm:text-sm">
                      <span style={{ color: 'var(--text-muted)' }}>URLs: </span>
                      <span className={`font-bold ${urlCount > 150 ? 'text-red-400' : ''}`} style={urlCount <= 150 ? { color: 'var(--accent)' } : {}}>{urlCount}</span>
                      <span className="ml-3 sm:ml-4" style={{ color: 'var(--text-muted)' }}>Required: </span>
                      <span className="font-bold" style={{ color: 'var(--accent)' }}>{urlCount} credits</span>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting || urlCount === 0 || urlCount > 150}
                      className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm w-full sm:w-auto"
                      data-testid="submit-urls-button"
                    >
                      {submitting ? <Spinner className="w-5 h-5 animate-spin" /> : <><Lightning weight="bold" className="w-5 h-5" />Submit for Indexing</>}
                    </button>
                  </div>
                </form>
              </div>

              {/* Recent Indexing */}
              {submissions.length > 0 && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-3 sm:p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="font-bold text-sm sm:text-base flex items-center gap-2"><Lightning weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />Recent Indexing</h2>
                  </div>
                  <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                    {submissions.slice(0, 3).map((sub, index) => (
                      <div key={sub.id} className="p-3 sm:p-4" data-testid={`recent-submission-${index}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>Batch #{submissions.length - index}</span>
                              <BatchStatusBadge createdAt={sub.created_at} status={sub.status} />
                            </div>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{new Date(sub.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                            <p className="font-mono text-xs mt-0.5" style={{ color: 'var(--accent)' }}>{sub.urls.length} URL{sub.urls.length > 1 ? 's' : ''}</p>
                          </div>
                          <button onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)} className="p-1.5 rounded-lg transition-colors flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>
                            {expandedSubmission === sub.id ? <CaretUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {expandedSubmission === sub.id && (
                          <div className="mt-3 rounded-lg p-2 max-h-52 overflow-y-auto" style={{ background: 'var(--bg-input)' }}>
                            <div className="space-y-1">
                              {(sub.indexing_results || sub.urls.map(u => ({ url: u }))).map((result, i) => (
                                <div key={i} className="flex items-center gap-1.5 p-1.5 rounded" style={{ background: 'var(--bg-card)' }}>
                                  <p className="font-mono text-[10px] sm:text-xs truncate flex-1 min-w-0" style={{ color: 'var(--text-secondary)' }}>{result.url}</p>
                                  <div className="flex items-center gap-0.5 flex-shrink-0">
                                    <button onClick={() => copyUrl(result.url, `${sub.id}-${i}`)} className="p-1 rounded hover:bg-white/10 transition-colors" style={{ color: 'var(--text-muted)' }} title="Copy URL">
                                      {copiedId === `${sub.id}-${i}` ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                    <a href={`https://www.google.com/search?q=site:${encodeURIComponent(result.url)}`} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-white/10 transition-colors" style={{ color: 'var(--accent)' }} title="Check indexing on Google">
                                      <MagnifyingGlass className="w-3.5 h-3.5" />
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {submissions.length > 3 && (
                    <div className="p-3 text-center border-t" style={{ borderColor: 'var(--border-main)' }}>
                      <button onClick={() => setActiveSection('history')} className="text-sm hover:underline" style={{ color: 'var(--accent)' }}>View all history</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ========= INDEXING HISTORY ========= */}
          {activeSection === 'history' && (
            <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
              <div className="p-3 sm:p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                <h2 className="text-base sm:text-lg font-bold">Indexing History</h2>
                <button onClick={fetchSubmissions} className="flex items-center gap-1.5 transition-colors text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refresh-history">
                  <ArrowClockwise className={`w-4 h-4 ${loadingSubmissions ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>

              {loadingSubmissions ? (
                <div className="p-12 text-center"><Spinner className="w-8 h-8 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
              ) : submissions.length === 0 ? (
                <div className="p-8 sm:p-12 text-center">
                  <Globe weight="duotone" className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3" style={{ color: 'var(--text-dimmed)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No submissions yet</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-dimmed)' }}>Submit your first URLs to get started</p>
                </div>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                  {submissions.map((sub, index) => (
                    <div key={sub.id} className="p-3 sm:p-4" data-testid={`submission-row-${index}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>Batch #{submissions.length - index}</span>
                            <span className="font-mono text-[10px] sm:text-xs" style={{ color: 'var(--accent)' }}>{sub.urls.length} URL{sub.urls.length > 1 ? 's' : ''}</span>
                            <BatchStatusBadge createdAt={sub.created_at} status={sub.status} />
                          </div>
                          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                            {new Date(sub.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })} IST
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                          <button onClick={() => refreshSubmissionStatus(sub.id)} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} title="Refresh status">
                            <ArrowClockwise className="w-4 h-4" />
                          </button>
                          <button onClick={() => copyAllUrls(sub.urls)} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} title="Copy all URLs" data-testid={`copy-all-${index}`}>
                            {copiedId === 'all' ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          </button>
                          <button onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid={`view-urls-${index}`}>
                            {expandedSubmission === sub.id ? <CaretUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      {expandedSubmission === sub.id && (
                        <div className="mt-3 rounded-lg p-2 sm:p-3 max-h-64 overflow-y-auto" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-main)' }}>
                          <div className="space-y-1.5">
                            {(sub.indexing_results || sub.urls.map(u => ({ url: u }))).map((result, i) => (
                              <div key={i} className="flex items-center gap-1.5 p-1.5 sm:p-2 rounded" style={{ background: 'var(--bg-card)' }}>
                                <div className="flex-1 min-w-0">
                                  <p className="font-mono text-[10px] sm:text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{result.url}</p>
                                  {result.stage && <IndexingStage stage={result.stage} createdAt={sub.created_at} />}
                                </div>
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  <button onClick={() => copyUrl(result.url, `${sub.id}-${i}`)} className="p-1 sm:p-1.5 rounded hover:bg-white/10 transition-colors" style={{ color: 'var(--text-muted)' }} title="Copy URL" data-testid={`copy-url-${index}-${i}`}>
                                    {copiedId === `${sub.id}-${i}` ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                  <a href={`https://www.google.com/search?q=site:${encodeURIComponent(result.url)}`} target="_blank" rel="noopener noreferrer" className="p-1 sm:p-1.5 rounded hover:bg-white/10 transition-colors" style={{ color: 'var(--accent)' }} title="Check indexing on Google" data-testid={`check-url-${index}-${i}`}>
                                    <MagnifyingGlass className="w-3.5 h-3.5" />
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ========= PURCHASE HISTORY ========= */}
          {activeSection === 'purchases' && (
            <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
              <div className="p-3 sm:p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                <h2 className="text-base sm:text-lg font-bold">Transaction History</h2>
                <button onClick={() => { fetchPurchaseHistory(); fetchCreditHistory(); }} className="flex items-center gap-1.5 transition-colors text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refresh-purchase-history">
                  <ArrowClockwise className="w-4 h-4" /> Refresh
                </button>
              </div>

              {allTransactions.length === 0 ? (
                <div className="p-8 sm:p-12 text-center">
                  <ShoppingCart weight="duotone" className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3" style={{ color: 'var(--text-dimmed)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No transactions yet</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-dimmed)' }}><Link to="/pricing" style={{ color: 'var(--accent)' }}>Buy credits</Link> to see your transaction history</p>
                </div>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                  {allTransactions.map((tx, index) => (
                    <div key={index} className="p-3 sm:p-4 flex items-center justify-between gap-2 sm:gap-4" data-testid={`transaction-row-${index}`}>
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        {tx.source === 'purchase' ? (
                          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-bg)' }}>
                            <CurrencyBtc weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
                          </div>
                        ) : (
                          <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tx.type === 'admin_add' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                            <PlusCircle weight="duotone" className={`w-4 h-4 sm:w-5 sm:h-5 ${tx.type === 'admin_add' ? 'text-emerald-400' : 'text-red-400'}`} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-xs sm:text-sm truncate">
                            {tx.source === 'purchase' ? `${(tx.plan_id || 'N/A').charAt(0).toUpperCase() + (tx.plan_id || 'N/A').slice(1)} Plan` : tx.description}
                          </p>
                          <p className="text-[10px] sm:text-xs" style={{ color: 'var(--text-muted)' }}>
                            {new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                        <span className={`font-bold text-xs sm:text-sm ${tx.credits > 0 || tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(tx.credits > 0 || tx.amount > 0) ? '+' : ''}{tx.credits || tx.amount}
                        </span>
                        {tx.source === 'purchase' ? getPaymentStatusBadge(tx.status) : (
                          <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded ${tx.type === 'admin_add' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                            {tx.type === 'admin_add' ? 'Added' : 'Sub'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ========= PROFILE ========= */}
          {activeSection === 'profile' && (
            <div className="space-y-4 sm:space-y-6 max-w-xl">
              {/* Profile Info */}
              <div className="border rounded-xl p-4 sm:p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                <h2 className="text-base sm:text-lg font-bold mb-4 flex items-center gap-2"><PencilSimple weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />Edit Profile</h2>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Name</label>
                    <input type="text" value={profileName} onChange={e => setProfileName(e.target.value)} className="input-field text-sm" data-testid="profile-name" />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Email</label>
                    <input type="email" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} className="input-field text-sm" data-testid="profile-email" />
                  </div>
                  <button onClick={handleSaveProfile} disabled={savingProfile} className="btn-primary flex items-center gap-2 text-sm w-full sm:w-auto justify-center" data-testid="save-profile-btn">
                    {savingProfile ? <Spinner className="w-4 h-4 animate-spin" /> : <CheckCircle weight="bold" className="w-4 h-4" />}
                    Save Changes
                  </button>
                </div>
              </div>

              {/* Change Password */}
              <div className="border rounded-xl p-4 sm:p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                <h2 className="text-base sm:text-lg font-bold mb-4 flex items-center gap-2"><Lock weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />Change Password</h2>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Current Password</label>
                    <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="input-field text-sm" data-testid="current-password" />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>New Password</label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="input-field text-sm" data-testid="new-password" />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Confirm New Password</label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="input-field text-sm" data-testid="confirm-password" />
                  </div>
                  <button onClick={handleChangePassword} disabled={changingPassword} className="btn-primary flex items-center gap-2 text-sm w-full sm:w-auto justify-center" data-testid="change-password-btn">
                    {changingPassword ? <Spinner className="w-4 h-4 animate-spin" /> : <Lock weight="bold" className="w-4 h-4" />}
                    Change Password
                  </button>
                </div>
              </div>

              {/* Account Info */}
              <div className="border rounded-xl p-4 sm:p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                <h2 className="text-base sm:text-lg font-bold mb-3">Account Info</h2>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Member since</span><span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span></div>
                  <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Role</span><span className="capitalize">{user?.role || 'user'}</span></div>
                  <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Credits</span><span className="font-bold" style={{ color: 'var(--accent)' }}>{user?.credits || 0}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
